import axios from '@/lib/axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Wishlist item interface

interface WishlistState {
  items: any[];
  loading: boolean;
  error: string | null;
}

const initialState: WishlistState = {
  items: [],
  loading: false,
  error: null,
};

// ✅ GET all wishlist products
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async () => {
    const res = await axios.get('/api/v2/wishlists');
    return res.data.data;
  }
);

// ✅ ADD product to wishlist
export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (productId: string) => {
    const res = await axios.get(
      `/api/v2/wishlists-add-product?product_id=${productId}`
    );
    return res.data.data;
  }
);

// ✅ REMOVE product from wishlist
export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId: string) => {
    await axios.get(`/api/v2/wishlists-remove-product?product_id=${productId}`);
    return productId;
  }
);

// ✅ CHECK if product is in wishlist (optional)
export const checkInWishlist = async (productId: string): Promise<boolean> => {
  const res = await axios.get(
    `/api/v2/wishlists-check-product?product_id=${productId}`
  );
  return res.data.data === true;
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearWishlist: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchWishlist.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.items = action.payload;
        }
      )
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch wishlist';
      })

      .addCase(addToWishlist.fulfilled, (state, action: PayloadAction<any>) => {
        const exists = state.items.some((item) => item.id === action.payload);
        if (!exists) state.items.push(action.payload);
      })

      .addCase(
        removeFromWishlist.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.items = state.items.filter(
            (item) => item.id !== action.payload
          );
        }
      );
  },
});

export const { clearWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;
